import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Download } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useAccountSpend } from '@/hooks/use-api'
import { explainError } from '@/lib/explain-error'
import { formatCurrency, formatDate } from '@/lib/utils'
import { exportToCSV } from '@/utils/csvExport'
import type { AccountSpendRow, SpendStatus } from '@/types'
import { SPEND_STATUS, pctLabel } from './spendStatus'

type SortKey = 'this_fytd' | 'change' | 'last_fy'

/** 24 months of billing as a small line; decorative (the numbers are in the row). */
function Sparkline({ values }: { values: number[] }) {
  const w = 96
  const h = 24
  const max = Math.max(...values, 0)
  const min = Math.min(...values, 0)
  const span = max - min || 1
  const pts = values
    .map((v, i) => `${(i / (values.length - 1)) * w},${h - ((v - min) / span) * h}`)
    .join(' ')
  const zeroY = h - ((0 - min) / span) * h
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      aria-hidden="true"
      focusable="false"
      className="text-primary"
    >
      <line x1="0" x2={w} y1={zeroY} y2={zeroY} stroke="currentColor" strokeOpacity="0.15" />
      <line
        x1={w / 2}
        x2={w / 2}
        y1="0"
        y2={h}
        stroke="currentColor"
        strokeOpacity="0.2"
        strokeDasharray="2 2"
      />
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  )
}

/**
 * Billed spend per account: this fiscal year to date against the same period last year.
 * Admin only (billings are company finance data). Backend: billingsAccountSpendService.js.
 */
export function SpendTracker({ ownerId }: { ownerId?: string }) {
  const { data, isLoading, isError, error } = useAccountSpend(
    ownerId ? { owner_id: ownerId } : undefined
  )
  const [status, setStatus] = useState<SpendStatus | 'all'>('all')
  const [sort, setSort] = useState<SortKey>('this_fytd')
  const [showAll, setShowAll] = useState(false)

  const rows = useMemo(() => {
    const list = (data?.rows ?? []).filter((r) => status === 'all' || r.status === status)
    const key = (r: AccountSpendRow) =>
      sort === 'change' ? Math.abs(r.change) : sort === 'last_fy' ? r.last_fy : r.this_fytd
    return [...list].sort((a, b) => key(b) - key(a))
  }, [data, status, sort])

  if (isLoading)
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          Loading billed spend…
        </CardContent>
      </Card>
    )
  if (isError || !data)
    return (
      <Card>
        <CardContent className="p-6 text-sm text-destructive">
          {explainError(error, "Billed spend couldn't be loaded. Reload the page to try again.")}
        </CardContent>
      </Card>
    )

  const t = data.totals
  const totalChange = t.this_fytd - t.last_same_period
  const visible = showAll ? rows : rows.slice(0, 50)
  const p = data.period

  const exportRows = () =>
    exportToCSV({
      filename: `billed-spend-FY${data.fiscal_year}.csv`,
      data: rows.map((r) => ({
        Account: r.account_name,
        Owner: r.owner_name ?? '',
        Status: SPEND_STATUS[r.status].label,
        [`FY${data.fiscal_year} to date (CAD)`]: r.this_fytd,
        'Same period last year (CAD)': r.last_same_period,
        'Change (CAD)': r.change,
        'Change (%)': r.change_pct ?? '',
        [`FY${data.fiscal_year - 1} (CAD)`]: r.last_fy,
        'Last billed': r.last_billed ? String(r.last_billed).slice(0, 10) : '',
      })),
    })

  const sortButton = (key: SortKey, label: string) => (
    <button
      type="button"
      onClick={() => setSort(key)}
      aria-pressed={sort === key}
      className={`underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded ${sort === key ? 'font-semibold text-foreground' : ''}`}
    >
      {label}
    </button>
  )

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle>Billed spend: FY{data.fiscal_year} to date vs last year</CardTitle>
            <CardDescription>
              QuickBooks billing (CAD, credits included), {formatDate(p.this_from)} to{' '}
              {formatDate(p.this_to)}, against the same days last year ({formatDate(p.last_from)} to{' '}
              {formatDate(p.last_to)}). Growing or declining means a change of{' '}
              {data.growth_threshold_pct}% or more. Admins only.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={exportRows} disabled={!rows.length}>
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            Export CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm">
          <span className="font-semibold">{formatCurrency(t.this_fytd, 'CAD')}</span> billed this
          year to date, against {formatCurrency(t.last_same_period, 'CAD')} at this point last year
          ({totalChange >= 0 ? 'up' : 'down'} {formatCurrency(Math.abs(totalChange), 'CAD')}
          {t.last_same_period ? `, ${pctLabel((totalChange / t.last_same_period) * 100)}` : ''}).
        </p>
        {t.unlinked_this_fytd > 0 && (
          <p className="rounded-md border p-3 text-sm" role="note">
            {formatCurrency(t.unlinked_this_fytd, 'CAD')} of this year&apos;s billing (
            {t.unlinked_customers} QuickBooks customer{t.unlinked_customers === 1 ? '' : 's'})
            isn&apos;t linked to a GRM account, so it isn&apos;t in the table below.{' '}
            <Link to="/sales-trackers/customer-accounts" className="underline">
              Link customers to accounts
            </Link>
          </p>
        )}

        <div
          className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6"
          role="group"
          aria-label="Filter by status"
        >
          {(['all', 'growing', 'steady', 'declining', 'lapsed', 'new'] as const).map((s) => {
            const b =
              s === 'all'
                ? {
                    accounts: data.rows.length,
                    this_fytd: data.rows.reduce((n, r) => n + r.this_fytd, 0),
                    last_same_period: data.rows.reduce((n, r) => n + r.last_same_period, 0),
                  }
                : data.by_status[s]
            const label = s === 'all' ? 'All accounts' : SPEND_STATUS[s].label
            return (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setStatus(s)
                  setShowAll(false)
                }}
                aria-pressed={status === s}
                className={`rounded-md border p-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${status === s ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}
              >
                <span className="block text-xs font-medium text-muted-foreground">{label}</span>
                <span className="block text-lg font-bold tabular-nums">{b.accounts}</span>
                <span className="block text-xs tabular-nums">
                  {formatCurrency(b.this_fytd, 'CAD')} this year
                </span>
                <span className="block text-xs text-muted-foreground tabular-nums">
                  {formatCurrency(b.last_same_period, 'CAD')} last year
                </span>
              </button>
            )
          })}
        </div>
        {status !== 'all' && (
          <p className="text-xs text-muted-foreground">{SPEND_STATUS[status].hint}</p>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm" aria-label="Billed spend by account">
            <thead>
              <tr className="border-b text-muted-foreground">
                <th scope="col" className="p-2 text-left font-medium">
                  Account
                </th>
                <th scope="col" className="p-2 text-left font-medium">
                  Owner
                </th>
                <th scope="col" className="p-2 text-right font-medium">
                  {sortButton('this_fytd', 'This year to date')}
                </th>
                <th scope="col" className="p-2 text-right font-medium">
                  Same period last year
                </th>
                <th scope="col" className="p-2 text-right font-medium">
                  {sortButton('change', 'Change')}
                </th>
                <th scope="col" className="p-2 text-right font-medium">
                  {sortButton('last_fy', `FY${data.fiscal_year - 1}`)}
                </th>
                <th scope="col" className="p-2 text-left font-medium">
                  <span className="sr-only">24-month trend</span>
                  <span aria-hidden="true">Trend</span>
                </th>
                <th scope="col" className="p-2 text-left font-medium">
                  Status
                </th>
                <th scope="col" className="p-2 text-left font-medium">
                  Last billed
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.account_id} className="border-b last:border-0">
                  <td className="p-2">
                    <Link to={`/accounts/${r.account_id}`} className="text-primary underline">
                      {r.account_name}
                    </Link>
                  </td>
                  <td className="p-2 text-muted-foreground">{r.owner_name ?? '–'}</td>
                  <td className="p-2 text-right tabular-nums">
                    {formatCurrency(r.this_fytd, 'CAD')}
                  </td>
                  <td className="p-2 text-right tabular-nums">
                    {formatCurrency(r.last_same_period, 'CAD')}
                  </td>
                  <td className="p-2 text-right tabular-nums">
                    {r.change >= 0 ? '+' : '−'}
                    {formatCurrency(Math.abs(r.change), 'CAD')}
                    {r.change_pct != null && (
                      <span className="block text-xs text-muted-foreground">
                        {pctLabel(r.change_pct)}
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-right tabular-nums">
                    {formatCurrency(r.last_fy, 'CAD')}
                  </td>
                  <td className="p-2">
                    <Sparkline values={r.months} />
                  </td>
                  <td className="p-2">
                    <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium">
                      {SPEND_STATUS[r.status].label}
                    </span>
                  </td>
                  <td className="p-2 whitespace-nowrap">
                    {r.last_billed ? formatDate(r.last_billed) : '–'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!rows.length && (
          <p className="text-sm text-muted-foreground">No accounts in this group.</p>
        )}
        {rows.length > 50 && (
          <Button variant="outline" size="sm" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Show the first 50' : `Show all ${rows.length}`}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}
